// Memory-only cache для OTP-флоу сброса пароля (docs/specs/auth-otp-verification).
//
// Несёт email между /forgot-password → /verify-reset-code и reset_token между
// /verify-reset-code → /auth/reset. reset_token — password-broker токен (одноразовый,
// 60 мин). Держим ТОЛЬКО в памяти (НЕ localStorage/URL): reload → state теряется →
// экраны делают fallback на /forgot-password. Idle 30 мин → Provider чистит сам.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type PasswordResetState = {
  email: string;
  resetToken?: string;
};

export type PasswordResetContextValue = {
  state: PasswordResetState | null;
  set: (s: PasswordResetState) => void;
  clear: () => void;
};

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

const PasswordResetContext = createContext<PasswordResetContextValue | null>(null);

export function PasswordResetProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PasswordResetState | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const set = useCallback(
    (s: PasswordResetState) => {
      setState(s);
      clearTimer();
      timeoutRef.current = setTimeout(() => setState(null), IDLE_TIMEOUT_MS);
    },
    [clearTimer],
  );

  const clear = useCallback(() => {
    setState(null);
    clearTimer();
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  const value = useMemo<PasswordResetContextValue>(
    () => ({ state, set, clear }),
    [state, set, clear],
  );

  return <PasswordResetContext.Provider value={value}>{children}</PasswordResetContext.Provider>;
}

export function usePasswordResetContext(): PasswordResetContextValue {
  const ctx = useContext(PasswordResetContext);
  if (!ctx) {
    throw new Error('usePasswordResetContext must be used within <PasswordResetProvider>');
  }
  return ctx;
}
